import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from '@tanstack/react-router'
import type { BlogPost } from '@/data/blog-posts'
import { Badge } from '@/components/ui/badge'
import { SectionContainer } from '@/components/layout/SectionContainer'
import { BlogPostCard } from '@/components/blog/BlogPostCard'
import { useBlogPostsPaged } from '@/hooks/useBlogQueries'
import { designSystem } from '@/lib/design-system'
import { getLocaleFromPath } from '@/lib/i18n-utils'
import { cn } from '@/lib/utils'

export type LabStatus =
  | 'inProgress'
  | 'prototype'
  | 'research'
  | 'planned'
  | 'submitted'

export function StatusLabel({
  status,
  qualifier,
}: {
  status: LabStatus
  qualifier?: 'simulated' | 'openSource'
}) {
  const { t } = useTranslation('lab')
  return (
    <Badge variant="outline" className="lab-status text-xs">
      {t(`status.${status}`)}
      {qualifier ? ` · ${t(`status.${qualifier}`)}` : ''}
    </Badge>
  )
}

export function LabPageHeader({
  eyebrow,
  title,
  intro,
  status,
}: {
  eyebrow: string
  title: string
  intro: string
  status?: ReactNode
}) {
  return (
    <div className="journal-width">
      <div className="lab-page-header">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <span className="lab-page-name">{eyebrow}</span>
          {status}
        </div>
        <h1 className="lab-page-title">{title}</h1>
        {intro ? (
          <p
            className={cn(
              designSystem.typography.body.lead,
              designSystem.typography.muted,
            )}
          >
            {intro}
          </p>
        ) : null}
      </div>
    </div>
  )
}

export function LabSection({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  return (
    <div className="journal-width">
      <div className="lab-section">
        <h2 className="lab-section-title">{title}</h2>
        <div className="lab-section-body">{children}</div>
      </div>
    </div>
  )
}

export function TaggedPosts({
  tag,
  title,
}: {
  tag: 'cloud' | 'research' | 'iot'
  title: string
}) {
  const { pathname } = useLocation()
  const locale = getLocaleFromPath(pathname)
  const { data, isError } = useBlogPostsPaged(locale, { tag, pageSize: 6 })
  const posts: BlogPost[] = data?.items ?? []
  if (isError || posts.length === 0) return null
  return (
    <div className="journal-width">
      <SectionContainer spacing="lg" title={title}>
        <div
          className={cn(
            designSystem.grid.responsive.three,
            designSystem.spacing.gap.lg,
          )}
        >
          {posts.map((post: BlogPost) => (
            <BlogPostCard key={post.slug} post={post} locale={locale} />
          ))}
        </div>
      </SectionContainer>
    </div>
  )
}
